import React, { useEffect, useMemo } from "react";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useTranslation } from "react-i18next";
import {
  DealConfidenceLevel,
  DealData,
  DealMilestone,
  Score as DealScoreType
} from "@/pages/deals/types";
import { getEnumTKey } from "@/utils/strings";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import Text from "@/components/ui/text";
import { useUsers } from "@/entities/users/hooks";
import {
  useDealQuestionnairesScores,
  useDealsQuestionnairesSettings
} from "@/pages/deals/hooks";
import { Add, Loader } from "@dreamhub/designsystem/icons";
import { formatNumber } from "@/utils/numbers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";
import { TFunction } from "i18next";
import { useKPIScores } from "@/services/hooks/useKPIScores";
import IconContainer from "@/components/ui/icon-container";
import KPIsContainer from "@/components/controls/KPIsContainer";
import { useIsDemoDeal } from "@/assets/mockupData";
import { DealScoreBadge } from "@/pages/deals/components/DealScoreBadge";
import {
  getScoreKey,
  SCORE_CONFIGS,
  ScoreConfig,
  ScoreType
} from "@/pages/deals/scoreConfig";
import SingleSelect from "@/components/controls/SingleSelect";
import { dealConfidenceLevels } from "@/pages/deals/helpers";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { find, includes, isNull, map } from "lodash";
import { Button } from "@/components/ui/button";
import { getStagesUpToCurrent } from "@/pages/deals/components/DealStages/utils";

interface Props {
  deal?: DealData;
  containerClassName?: string;
  onOpenQuestionnaire?: (questionnaireId?: string) => void;
  isQuickView?: boolean;
  onUpdateField: (field: string, newValue: string | number) => void;
}

export interface KPIConfig {
  [field: string]: {
    subtitle: string;
    formatter?: (value: DealScoreType) => string | JSX.Element | null;
    icon?: (value: number) => JSX.Element;
    isPercentage?: boolean;
  };
}

export const getScoreBadgeTooltipContent = (
  t: TFunction<"translation", undefined>,
  scoreKey: ScoreConfig,
  scoreType: ScoreType,
  dealScore?: DealScoreType | null
) => {
  switch (scoreType) {
    case ScoreType.STAKEHOLDERS_MAPPING:
      return t(`deals.kpis.scoreTypeTooltips.${scoreType}.percentage`, {
        part:
          scoreKey === ScoreConfig.PASS || scoreKey === ScoreConfig.FULL_PASS
            ? `${t("deals.kpis.all")}`
            : "",
        percentage:
          scoreKey !== ScoreConfig.PASS && scoreKey !== ScoreConfig.FULL_PASS
            ? dealScore?.scoreResult
            : "",
        isPercentage:
          scoreKey !== ScoreConfig.PASS && scoreKey !== ScoreConfig.FULL_PASS
            ? "%"
            : ""
      });
    case ScoreType.POC:
    case ScoreType.STAKEHOLDERS_APPROVAL:
      return t(`deals.kpis.scoreTypeTooltips.${scoreType}.${scoreKey}`);
    case ScoreType.CHAMPION_SCORE:
      // Champion score has specific tooltips for each state
      if (!isNull(dealScore?.reason)) {
        return t(`deals.kpis.scoreTypeTooltips.${scoreType}.mandatory`);
      }
      return t(`deals.kpis.scoreTypeTooltips.${scoreType}.${scoreKey}`, {
        percentage: dealScore?.scoreResult
      });
    default:
      if (!isNull(dealScore?.reason)) {
        return t(`deals.kpis.scoreTypeTooltips.${scoreType}.mandatory`);
      }
      if (scoreKey === ScoreConfig.PASS || scoreKey === ScoreConfig.FULL_PASS) {
        return t(`deals.kpis.scoreTypeTooltips.${scoreType}.pass`);
      }
      if (scoreKey === ScoreConfig.PENDING) {
        return t(`deals.kpis.scoreTypeTooltips.${scoreType}.pending`);
      }
      return t(`deals.kpis.scoreTypeTooltips.${scoreType}.percentage`, {
        only: scoreKey === ScoreConfig.FAILED ? `${t("deals.kpis.only")} ` : "",
        percentage: dealScore?.scoreResult
      });
  }
};

interface AddScoreProps {
  onClick: () => void;
  t: TFunction<"translation", undefined>;
  scoreType?: ScoreType;
}

const AddScoreButton: React.FC<AddScoreProps> = ({ onClick, t, scoreType }) => {
  return (
    <Button variant="tertiary" onClick={onClick}>
      <IconContainer size="5" color="current">
        <Add />
      </IconContainer>
      <Text variant="body" className="text-cta">
        {scoreType
          ? t(`deals.kpis.${SCORE_CONFIGS[scoreType].emptyText}`)
          : t("deals.kpis.addScore")}
      </Text>
    </Button>
  );
};

const DealKPIs: React.FC<Props> = ({
  deal,
  containerClassName,
  onOpenQuestionnaire,
  onUpdateField
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [managedBy] = useUsers(deal?.managedBy ? [deal?.managedBy] : []).users;
  const { fieldName: arrOrMrrField } = useArrMrr();

  const stages = useDealStages().data?.stages;

  const isDemo = useIsDemoDeal(deal?.id || "");

  const currentStage = useMemo(
    () => find(stages, (stage) => stage.id === deal?.stage),
    [stages, deal?.stage]
  );
  const currentMilestone = currentStage?.milestone || 1;
  const stagesUpToCurrent = useMemo(
    () => getStagesUpToCurrent(currentStage, stages),
    [currentStage, stages]
  );

  useEffect(() => {
    if (!deal?.id) return;
    const invalidateScores = () => {
      stagesUpToCurrent.forEach((stageId) => {
        queryClient.invalidateQueries({
          queryKey: [
            "deals/questionnaireScores",
            {
              method: "GET",
              pathParams: {
                dealId: deal.id,
                stageId: String(stageId)
              }
            }
          ],
          refetchType: "active",
          exact: true
        });
      });
    };

    invalidateScores();
  }, [deal?.id, stagesUpToCurrent, queryClient]);

  // Fetch questionnaires questions for all stages
  const { data: questionnairesSettings } =
    useDealsQuestionnairesSettings(stagesUpToCurrent);

  //Fetch all past stages questionnaires scores
  const { data: questionnairesScores, isLoading: isLoadingScores } =
    useDealQuestionnairesScores(deal?.id || "", stagesUpToCurrent);

  const kpiToScoreValueMap = useKPIScores({
    questionnairesSettings,
    questionnairesScores
  });

  if (!deal) {
    return null;
  }

  const handleAddScoreClick = (scoreType: ScoreType) => {
    if (!onOpenQuestionnaire) return;
    const questionnaireData = kpiToScoreValueMap[scoreType];
    onOpenQuestionnaire(questionnaireData?.questionnaireId);
  };

  // Check if a system questionnaire for a given KPI is deleted
  const isSystemQuestionnaireDeleted = (scoreType: ScoreType): boolean => {
    const systemQuestionnaire = Object.values(questionnairesSettings)
      .flatMap((response) => response?.data?.questionnaires || [])
      .find(
        (questionnaire) =>
          questionnaire.kpiValue === scoreType && questionnaire.isSystem
      );
    return systemQuestionnaire?.isDeleted === true;
  };

  const createScoreFormatter =
    (scoreType: ScoreType) => (value?: DealScoreType) => {
      if (isLoadingScores) {
        return (
          <IconContainer size="5">
            <Loader />
          </IconContainer>
        );
      }
      // Only show the "Add Score" button if value is completely missing
      if (!value) {
        return (
          <AddScoreButton
            t={t}
            scoreType={scoreType}
            onClick={() => handleAddScoreClick(scoreType)}
          />
        );
      }

      const scoreKey = getScoreKey(value.scoreText);
      const questionnaireData = kpiToScoreValueMap[scoreType];

      return (
        <Tooltip>
          <TooltipTrigger asChild className="cursor-pointer">
            <DealScoreBadge
              scoreText={value.scoreText}
              scoreType={scoreType}
              onClick={
                onOpenQuestionnaire
                  ? () =>
                      onOpenQuestionnaire(questionnaireData?.questionnaireId)
                  : undefined
              }
              textVariant="label"
              tabIndex={0}
              role={onOpenQuestionnaire ? "button" : undefined}
            />
          </TooltipTrigger>
          <TooltipContent>
            {getScoreBadgeTooltipContent(t, scoreKey, scoreType, value)}
          </TooltipContent>
        </Tooltip>
      );
    };

  const kpiConfig: KPIConfig = {
    ...(isDemo
      ? {
          dreamerProbabilityToWin: {
            subtitle: t("deals.kpis.dreamerProbabilityToWin"),
            formatter: (_value) => "54%"
          }
        }
      : {}),
    aeConfidenceLevel: {
      subtitle: t("deals.kpis.aeConfidenceLevel"),
      formatter: (value) => (
        <SingleSelect
          fieldName="aeConfidenceLevel"
          variant="inline"
          options={map(dealConfidenceLevels, (level) => ({
            id: level,
            name: DealConfidenceLevel[level]
          }))}
          formatter={(value) =>
            t(
              getEnumTKey(
                "deals",
                "levels",
                DealConfidenceLevel,
                value as number
              )
            )
          }
          selectedValue={Number(value)}
          renderIcon={(value) => <ConfidenceLevelIcon value={Number(value)} />}
          onSelection={(newValue) =>
            onUpdateField("aeConfidenceLevel", newValue)
          }
          triggerVariant="smSubtitle"
          customEmptyTriggerComponent={
            <div className="pt-2">
              <AddScoreButton t={t} onClick={() => {}} />
            </div>
          }
        />
      )
    },
    percentageFromOwnerPipeline: {
      subtitle: t("deals.kpis.percentageFromOwnerPipeline", {
        userName: managedBy
          ? managedBy?.firstName
          : t("deals.kpis.defaultOwner")
      }),
      formatter: (value) =>
        formatNumber(Number(value) || 0, 2, true, false, true).value,
      isPercentage: true
    },
    percentageOfMappedContacts: {
      subtitle: t("deals.kpis.percentageOfMappedContacts"),
      formatter: (value) =>
        isDemo
          ? "72"
          : formatNumber(Number(value) || 0, 0, true, false, true).value,
      isPercentage: true
    },
    qualification: {
      subtitle: t("deals.kpis.qualificationScore"),
      formatter: createScoreFormatter(ScoreType.QUALIFICATION)
    },
    stakeholdersMapping: {
      subtitle: t("deals.kpis.stakeholdersScore"),
      formatter: createScoreFormatter(ScoreType.STAKEHOLDERS_MAPPING)
    },
    championScore: {
      subtitle: t("deals.kpis.championScore"),
      formatter: createScoreFormatter(ScoreType.CHAMPION_SCORE)
    },
    poc: {
      subtitle: t("deals.kpis.pocStatus"),
      formatter: createScoreFormatter(ScoreType.POC)
    },
    stakeholdersApproval: {
      subtitle: "",
      formatter: createScoreFormatter(ScoreType.STAKEHOLDERS_APPROVAL)
    }
  };

  const fixedKPIs = [
    ...(isDemo
      ? ["dreamerProbabilityToWin"]
      : []),
    "aeConfidenceLevel",
    "percentageFromOwnerPipeline",
    "percentageOfMappedContacts"
  ];

  const getMilestoneKPIs = (milestone: DealMilestone) => {
    const kpisAfterQualification = ["stakeholdersMapping", "championScore"];
    const shouldShowQualificationKPI =
      kpiToScoreValueMap[
        ScoreType.QUALIFICATION
      ]?.score?.scoreText?.toLowerCase() !== "pass";
    let kpis: string[] = [];
    switch (milestone) {
      case DealMilestone.QUALIFICATION:
        kpis = ["qualification"];
        break;
      case DealMilestone.PPP:
        kpis = [...kpisAfterQualification];
        break;
      default:
        kpis = [...kpisAfterQualification, "poc_and_approval"]; // Special combined key for POC and approval
    }
    if (
      shouldShowQualificationKPI &&
      milestone !== DealMilestone.QUALIFICATION
    ) {
      kpis = ["qualification", ...kpis];
    }
    return kpis;
  };

  const fieldKeys = [...fixedKPIs, ...getMilestoneKPIs(currentMilestone)];

  return (
    <KPIsContainer className={containerClassName}>
      {map(fieldKeys, (field) => {
        if (field === "poc_and_approval") {
          const isPocDeleted = isSystemQuestionnaireDeleted(ScoreType.POC);
          const isStakeholdersApprovalDeleted = isSystemQuestionnaireDeleted(
            ScoreType.STAKEHOLDERS_APPROVAL
          );
          if (isPocDeleted && isStakeholdersApprovalDeleted) {
            return null;
          }

          const pocScore = kpiToScoreValueMap[ScoreType.POC]?.score;
          const stakeholderApprovalScore =
            kpiToScoreValueMap[ScoreType.STAKEHOLDERS_APPROVAL]?.score;
          const shouldRenderStakeholdersApproval =
            pocScore &&
            pocScore.scoreResult > 0 &&
            !isStakeholdersApprovalDeleted;
          return (
            <div key={field} className="min-w-0">
              <div className="flex flex-col gap-2 p-3">
                <div className="flex flex-wrap gap-2 items-center min-h-7">
                  {!isPocDeleted && (
                    <Text as="span" variant="body">
                      {kpiConfig.poc.formatter?.(pocScore as DealScoreType)}
                    </Text>
                  )}
                  {shouldRenderStakeholdersApproval && (
                    <div className="flex items-center">
                      <Text as="span" variant="body">
                        {kpiConfig?.stakeholdersApproval.formatter?.(
                          stakeholderApprovalScore as DealScoreType
                        )}
                      </Text>
                    </div>
                  )}
                </div>
                {!isPocDeleted && (
                  <Text variant="bodySecondary">{kpiConfig.poc.subtitle}</Text>
                )}
              </div>
            </div>
          );
        }

        const isScoreKPI = !includes(fixedKPIs, field);
        if (isScoreKPI && isSystemQuestionnaireDeleted(field as ScoreType)) {
          return null;
        }

        const fieldConfig = kpiConfig[field];

        const getPercentageFromOwnerPipelineValue = (deal: any) => {
          const capitalizedField =
            arrOrMrrField.charAt(0).toUpperCase() + arrOrMrrField.slice(1);
          return deal?.[`percentageFromOwnerPipeline${capitalizedField}`];
        };

        let value: number | DealScoreType = isScoreKPI
          ? kpiToScoreValueMap[field as ScoreType]?.score
          : field === "percentageFromOwnerPipeline"
            ? getPercentageFromOwnerPipelineValue(deal)
            : deal[field];

        return (
          <div key={field} className="flex flex-col gap-2 p-3 min-w-0">
            <div className="flex gap-1 items-center h-7">
              {fieldConfig.icon &&
                typeof value === "number" &&
                fieldConfig.icon?.(value as number)}
              <Text as="span" variant={isScoreKPI ? "body" : "smSubtitle"}>
                {fieldConfig.formatter &&
                  fieldConfig.formatter?.(value as DealScoreType)}
                {fieldConfig.isPercentage ? "%" : ""}
              </Text>
            </div>
            <Text variant={"bodySecondary"}>{fieldConfig.subtitle}</Text>
          </div>
        );
      })}
    </KPIsContainer>
  );
};

export default DealKPIs;
